import { useLayoutEffect, useRef, useState } from "react";

/** Pixels reserved for dropdown arrow, padding, and end adornment inside the outlined field. */
export const TAG_FIELD_WIDTH_RESERVED_PX = 56;

/** Estimate chip width (px) for tag pills — used to fit chips before +N when collapsed. */
export const measureTagChipWidth = (() => {
  let canvas;
  return (label) => {
    if (typeof document === "undefined") return 100;
    if (!canvas) {
      canvas = document.createElement("canvas");
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return 100;
    ctx.font = '400 13px "Open Sans", "Helvetica Neue", Arial, sans-serif';
    const text = label || "";
    const labelW = Math.min(120, Math.ceil(ctx.measureText(text).width));
    return labelW + 46;
  };
})();

/** How many leading tag chips fit in `availableWidthPx`, reserving space for a +N suffix when some tags are hidden. */
export const countTagChipsThatFit = (availableWidthPx, tags, measureChip) => {
  if (!tags?.length || availableWidthPx <= 0) return tags?.length ?? 0;
  const gap = 4;
  const suffixWidth = (hidden) => 8 + 22 + String(hidden).length * 9;

  for (let k = tags.length; k >= 1; k--) {
    let total = 0;
    for (let i = 0; i < k; i++) {
      total += measureChip(tags[i]);
      if (i < k - 1) total += gap;
    }
    const hidden = tags.length - k;
    if (hidden > 0) total += gap + suffixWidth(hidden);
    if (total <= availableWidthPx) return k;
  }
  return 1;
};

/**
 * Merges a ref callback with MUI TextField `InputProps.ref` (function or object ref).
 */
export const mergeMuiOutlinedInputRef = (outerRef, muiInputPropsRef) => (node) => {
  outerRef.current = node;
  if (typeof muiInputPropsRef === "function") muiInputPropsRef(node);
  else if (muiInputPropsRef) muiInputPropsRef.current = node;
};

/**
 * Which tag chips to render when the field is collapsed vs expanded (open or focused).
 */
export const getTagChipVisibility = (value, { tagOpen, tagsInputFocused, collapsedVisibleTagCount }) => {
  const showAll = tagOpen || tagsInputFocused;
  const maxVisible = showAll ? value.length : Math.min(collapsedVisibleTagCount, value.length);
  const visible = showAll ? value : value.slice(0, maxVisible);
  const hiddenCount = showAll ? 0 : value.length - visible.length;
  return { visible, hiddenCount, showAll };
};

/**
 * Measures the tags outlined input width and computes how many chips fit before +N when not focused/open.
 *
 * @param {Object} params
 * @param {boolean} params.tagOpen - Autocomplete open
 * @param {boolean} params.tagsInputFocused - input focused
 * @param {Array} params.selectedTags - selected tag objects (stable reference recommended via useMemo)
 */
export function useTagFieldCollapsedVisibleCount({ tagOpen, tagsInputFocused, selectedTags }) {
  const tagsInputRootRef = useRef(null);
  const [tagsInputWidth, setTagsInputWidth] = useState(0);
  const [collapsedVisibleTagCount, setCollapsedVisibleTagCount] = useState(Infinity);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    let ro;
    const attach = () => {
      const el = tagsInputRootRef.current;
      if (!el) return false;
      ro = new ResizeObserver(() => {
        setTagsInputWidth(el.clientWidth);
      });
      ro.observe(el);
      setTagsInputWidth(el.clientWidth);
      return true;
    };
    if (!attach()) {
      const id = requestAnimationFrame(() => {
        attach();
      });
      return () => {
        cancelAnimationFrame(id);
        ro?.disconnect();
      };
    }
    return () => ro?.disconnect();
  }, [selectedTags?.length, tagOpen, tagsInputFocused]);

  useLayoutEffect(() => {
    const showAll = tagOpen || tagsInputFocused;
    if (showAll) {
      setCollapsedVisibleTagCount(selectedTags?.length ?? 0);
      return;
    }
    const tags = selectedTags || [];
    if (!tags.length) {
      setCollapsedVisibleTagCount(0);
      return;
    }
    const el = tagsInputRootRef.current;
    const w = el?.clientWidth ?? tagsInputWidth;
    if (!w) {
      setCollapsedVisibleTagCount(tags.length);
      return;
    }
    const available = Math.max(48, w - TAG_FIELD_WIDTH_RESERVED_PX);
    const n = countTagChipsThatFit(available, tags, (opt) => measureTagChipWidth(opt?.name));
    setCollapsedVisibleTagCount(Math.max(1, Math.min(n, tags.length)));
  }, [tagOpen, tagsInputFocused, selectedTags, tagsInputWidth]);

  return { tagsInputRootRef, collapsedVisibleTagCount };
}
